import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Users, Search, Building2, ShoppingCart,
  ChevronLeft, ChevronRight, UserCheck, UserX,
  KeyRound, Copy, RefreshCw, Loader2, Check
} from 'lucide-react';

const genPassword = () => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const digits = '23456789';
  const symbols = '@#$%';
  const all = upper + lower + digits + symbols;
  const pick = (set) => set[Math.floor(Math.random() * set.length)];
  let pw = pick(upper) + pick(lower) + pick(digits) + pick(symbols);
  for (let i = 0; i < 8; i++) pw += pick(all);
  return pw.split('').sort(() => Math.random() - 0.5).join('');
};

export default function AdminTenantsPage() {
  const { authHeaders, API } = useAdminAuth();
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [generatedPw, setGeneratedPw] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tenants`, {
        ...authHeaders(),
        params: { search, page, limit: 15 },
      });
      setTenants(res.data.tenants);
      setTotal(res.data.total);
    } catch {}
    setLoading(false);
  }, [API, authHeaders, search, page]);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const viewTenant = async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/tenants/${id}`, authHeaders());
      setSelectedTenant(res.data);
    } catch {}
    setDetailLoading(false);
  };

  const toggleTenant = async (id) => {
    try {
      await axios.put(`${API}/tenants/${id}`, {}, authHeaders());
      fetchTenants();
      if (selectedTenant?.id === id) viewTenant(id);
    } catch {}
  };

  const openResetPw = () => {
    setGeneratedPw(genPassword());
    setResetDone(false);
    setCopied(false);
    setShowResetPw(true);
  };

  const confirmResetPw = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/tenants/${selectedTenant.id}/reset-password`, { new_password: generatedPw }, authHeaders());
      setResetDone(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
    setResetting(false);
  };

  const copyPw = () => {
    navigator.clipboard.writeText(generatedPw).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const timeAgo = (iso) => {
    if (!iso) return 'N/A';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  const totalPages = Math.ceil(total / 15);

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-tenants-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">{t('admin.tenants.title')}</h1>
            <p className="text-sm text-slate-500">{total} {t('admin.tenants.registered')}</p>
          </div>
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder={t('admin.tenants.searchPlaceholder')}
            data-testid="tenant-search-input"
            className="ps-9 bg-[#0d1224] border-slate-800/60 text-white placeholder:text-slate-600"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Table */}
          <div className="lg:col-span-2 bg-[#0d1224] border border-slate-800/60 rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : tenants.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('admin.tenants.noTenants')}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tenants-table">
                    <thead>
                      <tr className="border-b border-slate-800/60">
                        <th className="text-start px-4 py-3 text-xs font-medium text-slate-500">{t('admin.tenants.tenant')}</th>
                        <th className="text-start px-4 py-3 text-xs font-medium text-slate-500 hidden md:table-cell">{t('admin.tenants.branches')}</th>
                        <th className="text-start px-4 py-3 text-xs font-medium text-slate-500 hidden md:table-cell">{t('admin.tenants.orders')}</th>
                        <th className="text-start px-4 py-3 text-xs font-medium text-slate-500 hidden lg:table-cell">{t('admin.tenants.lastActive')}</th>
                        <th className="text-start px-4 py-3 text-xs font-medium text-slate-500">{t('admin.tenants.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.map(tn => (
                        <tr
                          key={tn.id}
                          onClick={() => viewTenant(tn.id)}
                          data-testid={`tenant-row-${tn.id}`}
                          className={`
                            border-b border-slate-800/30 cursor-pointer transition-colors
                            ${selectedTenant?.id === tn.id ? 'bg-red-500/5' : 'hover:bg-slate-800/30'}
                          `}
                        >
                          <td className="px-4 py-3">
                            <p className="font-medium text-white text-sm">{tn.name}</p>
                            <p className="text-xs text-slate-500">{tn.email}</p>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="text-slate-300">{tn.branches_count}</span>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="text-slate-300">{tn.orders_count}</span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <span className="text-slate-400 text-xs">{timeAgo(tn.last_activity)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium
                              ${tn.is_active !== false ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}
                            `}>
                              {tn.is_active !== false ? t('admin.tenants.active') : t('admin.tenants.disabled')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800/60">
                    <span className="text-xs text-slate-500">{t('admin.tenants.page')} {page} {t('admin.tenants.of')} {totalPages}</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                        className="text-slate-400 h-7 w-7 p-0"><ChevronLeft className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                        className="text-slate-400 h-7 w-7 p-0"><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Detail Panel */}
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-4" data-testid="tenant-detail-panel">
            {detailLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : selectedTenant ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-white">{selectedTenant.name}</h2>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => toggleTenant(selectedTenant.id)}
                    data-testid="toggle-tenant-btn"
                    className={selectedTenant.is_active !== false
                      ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                      : 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10'}
                  >
                    {selectedTenant.is_active !== false ? (
                      <><UserX className="w-3.5 h-3.5 me-1" /> {t('admin.tenants.disable')}</>
                    ) : (
                      <><UserCheck className="w-3.5 h-3.5 me-1" /> {t('admin.tenants.enable')}</>
                    )}
                  </Button>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between text-slate-400">
                    <span>{t('admin.tenants.email')}</span>
                    <span className="text-slate-300">{selectedTenant.email}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>{t('admin.tenants.subscription')}</span>
                    <span className="text-slate-300">{selectedTenant.subscription_status || 'trial'}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>{t('admin.tenants.registered_date')}</span>
                    <span className="text-slate-300">{new Date(selectedTenant.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                <Button
                  size="sm" variant="outline" onClick={openResetPw} data-testid="reset-tenant-password-btn"
                  className="w-full h-8 text-xs bg-transparent border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                >
                  <KeyRound className="w-3.5 h-3.5 me-1.5" /> {t('admin.tenants.resetPassword')}
                </Button>

                {showResetPw && (
                  <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 space-y-2" data-testid="reset-password-panel">
                    <p className="text-[11px] text-slate-400">{resetDone ? t('admin.tenants.resetDoneHint') : t('admin.tenants.resetHint')}</p>
                    <div className="flex items-center gap-1.5">
                      <code dir="ltr" className="flex-1 px-2 py-1.5 rounded bg-slate-900 text-emerald-400 text-xs font-mono tracking-wide select-all" data-testid="generated-password">
                        {generatedPw}
                      </code>
                      <button onClick={copyPw} data-testid="copy-password-btn" className="p-1.5 rounded bg-slate-800 text-slate-300 hover:text-white" title={t('admin.tenants.copy')}>
                        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      {!resetDone && (
                        <button onClick={() => setGeneratedPw(genPassword())} data-testid="regen-password-btn" className="p-1.5 rounded bg-slate-800 text-slate-300 hover:text-white" title={t('admin.tenants.regenerate')}>
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {resetDone ? (
                      <div className="flex items-center gap-1.5 text-emerald-400 text-xs" data-testid="reset-success-msg">
                        <Check className="w-3.5 h-3.5" /> {t('admin.tenants.resetSuccess')}
                      </div>
                    ) : (
                      <div className="flex gap-1.5">
                        <Button size="sm" onClick={confirmResetPw} disabled={resetting} data-testid="confirm-reset-password-btn"
                          className="flex-1 h-7 text-xs bg-amber-600 hover:bg-amber-500 text-white">
                          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('admin.tenants.confirmReset')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setShowResetPw(false)} className="h-7 text-xs text-slate-400">
                          {t('common.cancel')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <h3 className="text-xs font-semibold text-slate-400 mb-2 flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> {t('admin.tenants.branches')} ({selectedTenant.branches?.length || 0})
                  </h3>
                  <div className="space-y-1">
                    {(selectedTenant.branches || []).map(b => (
                      <div key={b.id} className="px-2 py-1.5 bg-slate-800/30 rounded text-xs text-slate-300">{b.name}</div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-slate-400 mb-2 flex items-center gap-1">
                    <Users className="w-3 h-3" /> {t('admin.tenants.team')} ({selectedTenant.team_members?.length || 0})
                  </h3>
                  <div className="space-y-1">
                    {(selectedTenant.team_members || []).map(m => (
                      <div key={m.id} className="px-2 py-1.5 bg-slate-800/30 rounded text-xs flex justify-between">
                        <span className="text-slate-300">{m.name}</span>
                        <span className="text-slate-500">{m.role}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-slate-400 mb-2 flex items-center gap-1">
                    <ShoppingCart className="w-3 h-3" /> {t('admin.tenants.recentOrders')}
                  </h3>
                  <div className="space-y-1">
                    {(selectedTenant.recent_orders || []).slice(0, 5).map(o => (
                      <div key={o.id} className="px-2 py-1.5 bg-slate-800/30 rounded text-xs flex justify-between">
                        <span className="text-slate-300">#{o.order_number || o.id?.slice(0, 6)}</span>
                        <span className="text-slate-500">{o.status}</span>
                      </div>
                    ))}
                    {(!selectedTenant.recent_orders || selectedTenant.recent_orders.length === 0) && (
                      <p className="text-xs text-slate-600">{t('admin.tenants.noOrders')}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">{t('admin.tenants.selectTenant')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
